import React, { useState } from 'react';
import { Mail, MapPin, Send, Phone, MessageSquare, Check } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API}/contact-submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit contact form');
      }

      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', subject: '', message: '' });
      }, 4000);
    } catch (err) {
      setError('Unable to submit message. Please try again.');
      console.error('Contact form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: 'contact@elbethel.online',
      description: 'Send us a message and we\'ll respond as soon as possible'
    },
    {
      icon: MapPin,
      title: 'Mailing Address',
      details: '1910 Pacific Ave, Ste 2000-1947\nDallas, TX 75201',
      description: 'Our administrative office location'
    },
    {
      icon: MessageSquare,
      title: 'Prayer Requests',
      details: 'Submit prayer requests anytime',
      description: 'Our prayer team will pray over your submitted requests'
    }
  ];

  const ministryAreas = [
    { name: 'General Inquiry', email: 'contact@elbethel.online' },
    { name: 'Prayer Support', email: 'prayer@elbethel.online' },
    { name: 'Ministry Partnership', email: 'partnership@elbethel.online' },
    { name: 'Media Requests', email: 'media@elbethel.online' }
  ];

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Hero Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-purple-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 lg:mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 font-display">
              Contact Us
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto font-sans">
              We'd love to hear from you! Whether you have questions, prayer requests, 
              or want to get involved in ministry, we're here to connect with you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-6 font-display">
                Send Us a Message
              </h2>
              
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                        Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your full name"
                        className="w-full p-3 lg:p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your.email@example.com"
                        className="w-full p-3 lg:p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="What's this about?"
                      className="w-full p-3 lg:p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us how we can help you or what's on your heart..."
                      rows={6}
                      className="w-full p-3 lg:p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent resize-none font-sans"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#f17b21] hover:bg-[#d86919] text-white px-6 lg:px-8 py-3 lg:py-4 rounded-lg font-semibold transition-colors duration-300 flex items-center justify-center space-x-2 font-sans disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                      <p className="text-red-600 text-sm font-sans">{error}</p>
                    </div>
                  )}
                </form>
              ) : (
                <div className="text-center py-8 lg:py-12">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-4 font-display">Message Sent!</h3>
                  <p className="text-gray-600 font-sans">
                    Thank you for reaching out. We'll get back to you as soon as possible.
                  </p>
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div className="space-y-6 lg:space-y-8">
              {/* Contact Methods */}
              <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8">
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-6 font-display">Get in Touch</h3>
                
                <div className="space-y-6">
                  {contactInfo.map((info, index) => {
                    const IconComponent = info.icon;
                    return (
                      <div key={index} className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-[#f17b21] rounded-full flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-1 font-sans">{info.title}</h4>
                          <p className="text-[#f17b21] font-medium mb-2 font-sans whitespace-pre-line">{info.details}</p>
                          <p className="text-gray-600 text-sm font-sans">{info.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Ministry Departments */}
              <div className="bg-gray-50 rounded-2xl p-6 lg:p-8">
                <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-6 font-display">Ministry Departments</h3>
                <div className="space-y-4">
                  {ministryAreas.map((area, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-white rounded-lg">
                      <span className="font-semibold text-gray-800 font-sans text-sm lg:text-base">{area.name}</span>
                      <a 
                        href={`mailto:${area.email}`}
                        className="text-[#f17b21] hover:text-[#d86919] font-medium text-sm transition-colors duration-300 font-sans"
                      >
                        {area.email}
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gradient-to-r from-[#f17b21] to-[#ff9649] rounded-2xl p-6 lg:p-8 text-white">
                <h3 className="text-xl lg:text-2xl font-bold mb-4 font-display">We're Here to Help</h3>
                <p className="text-white/90 font-sans">
                  Whether you have questions about our ministry, need prayer support, or want to get involved, 
                  we're committed to responding to all inquiries with care and attention.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;